import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { hasPermission } from 'utils/permissions/permissions';
import CustomNavigationButtons from 'components/CustomNavigationButtons/CustomNavigationButtons';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import NumberDetails from 'views/Numbers/Details/NumberDetails';
import UsagePage from 'views/Numbers/Details/Usage';
import Services from 'views/Numbers/Details/Services';
import SimManagement from 'views/Numbers/Details/SimManagement';
import Settings from './Settings';
import PhoneInTalkOutlined from '@material-ui/icons/PhoneInTalkOutlined';
import LocalPhoneOutlined from '@material-ui/icons/LocalPhoneOutlined';
import SdCard from '@material-ui/icons/SdCardOutlined';
import Public from '@material-ui/icons/Public';
import Build from '@material-ui/icons/BuildOutlined';
import Info from '@material-ui/icons/Info';
import { Switch, Route, useLocation } from 'react-router-dom';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetailsPageStyle';
import { makeStyles } from '@material-ui/core';
import Wrapper from 'components/Wrapper/Wrapper';
import { AccountsService } from 'services/accounts/accounts';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch } from 'react-redux';
import { translate } from 'components/Translation';
import { decode, encode } from 'utils/crypto/crypto';
import invalidateCache from 'utils/api/invalidateCache';
import { upIdSelector, userIdSelector, userTypeSelector } from 'redux/modules/session/selectors';
import { CustomerAccount } from 'utils/api/models/accounts/customerAccountResponse';
import { ITab } from 'components/CustomNavigationButtons/CustomNavigationButtons';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { ProductsService } from 'services/products/products';
import {
	AAMALI_PLUS_ACCOUNT_CATEGORY,
	AAMALI_PLUS_SERVICES_TAB,
	NUMBER_DETAILS_TAB,
	SIM_DISCONNECTION_PRODUCTS,
	queryParams,
} from '../constants';
import { CustomerProducts } from 'utils/api/models/products/customerProducts';
import { finalize } from 'rxjs/operators';
import ActiveCommitments from './ActiveCommitments';
import { AccountType } from 'utils/api/models/accounts/accountType';
import PaymentService from 'services/payments/payment';
import { CommitmentDetails } from 'utils/api/models/payments/commitmentDetails';
import AamaliServices from './AamaliServices';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { isAamaliPlus } from 'utils/killswitch';
import { TagManager } from 'commons/gtm/tagManager';
import { getLanguage } from 'utils/language/language';

// @ts-ignore
const useStyles = makeStyles(styles);

const ACCOUNT_CATEGORY = 'ACCOUNT_CATEGORY';

interface INumberDetailsPageProps {
	match: {
		params: {
			filterBy: string;
			phoneNumberEncoded: string;
			accountNumber: string;
			accountType: string;
			billingType: string;
			upAccountId: string;
			subpage: string;
		};
		path: string;
	};
	location: {
		state: {
			accountCategory?: string;
		};
	};
}

const Details = ({ match, location }: INumberDetailsPageProps) => {
	const remoteConfig = useSelector(remoteConfigSelector);
	//kill switch to check if aamali services are to be shown
	const showAamali = isAamaliPlus(remoteConfig);
	let {
		filterBy,
		phoneNumberEncoded,
		accountNumber,
		accountType,
		billingType,
		upAccountId,
		subpage = location?.state?.accountCategory === AAMALI_PLUS_ACCOUNT_CATEGORY && showAamali
			? AAMALI_PLUS_SERVICES_TAB
			: NUMBER_DETAILS_TAB,
	} = match.params;
	const phoneNumber: string = decode(phoneNumberEncoded);
	const [loadingAccounts, setLoadingAccounts] = useState<boolean>(true);
	const [loadingCustomerProducts, setLoadingCustomerProducts] = useState<boolean>(true);
	const [account, setAccount] = useState<CustomerAccount | null>(null);
	const [customerProducts, setCustomerProducts] = useState<CustomerProducts | null>(null);
	const [commitmentDetails, setCommitmentDetails] = useState<CommitmentDetails[]>([]);
	//Aamali Plus
	const [isNumberAamaliPlus, setAamaliPlus] = useState(false);
	const [accCategory, setAccCategory] = useState('');
	const [accountName, setAccountName] = useState('');
	const isMobile: boolean = accountType.toLowerCase() === 'mobile';
	const isFix: boolean = accountType.toLowerCase() === 'fix';
	const isFbb: boolean = accountType.toLowerCase() === 'internet';
	const isWfbb: boolean = accountType.toLowerCase() === 'wfbb';
	const isPostpaid: boolean = billingType.toLowerCase() === 'postpaid';
	const userType: string = useSelector(userTypeSelector);
	const dispatch = useDispatch();
	const isAccountSuspended = Number(account?.customer.collectionIndicator) === 1;
	const showNumberDetails: boolean = hasPermission(userType, 'number-details:see');
	const showUsage: boolean = hasPermission(userType, 'number-usage:see') && !isWfbb;
	const showSimManagement: boolean = hasPermission(userType, 'number-sim-management:see') && isMobile && !isFix;
	const showSettings: boolean = hasPermission(userType, 'number-settings:see') && isFbb;
	const showMicroBundle: boolean =
		hasPermission(userType, 'number-aaamaliplus-services:see') && isFbb && isNumberAamaliPlus && showAamali; //isFbb will chheck for internet type, isNumberAamaliPlus means the accountCategory is 19, showAamali is for the killswitch. If all these conditions are true, only then we'll be able to see aamali+ services tab
	const hasActiveCommitments = useMemo(() => {
		return commitmentDetails?.[0]?.commitmentDetails?.length > 0;
	}, [commitmentDetails]);

	const showActiveCommitments: boolean =
		hasPermission(userType, 'number-active-commitments:see') && hasActiveCommitments && isPostpaid;
	const filter: string = filterBy ? `/${filterBy}` : '';
	const path: string = `/dashboard/numbers${filter}/${encode(
		phoneNumber
	)}/${accountNumber}/${accountType}/${billingType}/${upAccountId}`;
	const classes = useStyles();

	const accId = useSelector(upIdSelector);
	const userId = useSelector(userIdSelector);

	const adslLogin: string = useMemo(() => {
		if (account) {
			const productCharacteristic = account.product.productCharacteristic.find(
				({ name }) => name === 'ADSL_LOGIN'
			);
			return productCharacteristic?.value || '';
		}
		return '';
	}, [account]);

	const accountCategory = useMemo(() => {
		return (account?.customer?.customerAccount?.customer?.characteristic || []).find(
			(c) => c.name === ACCOUNT_CATEGORY
		)?.value;
	}, [account]);

	const isSimCardDisconnected = useMemo(() => {
		const productsIds = customerProducts?.products.map((singleProduct) => singleProduct.legacyProductId) || [];
		return SIM_DISCONNECTION_PRODUCTS.every((singleProduct) => {
			return productsIds.includes(singleProduct.id);
		});
	}, [customerProducts]);

	const showServices: boolean =
		hasPermission(userType, 'number-services:see') && !isAccountSuspended && !isSimCardDisconnected && !isWfbb;

	const resourceInfo = customerProducts?.resourceInfo || null;

	const fetchCustomerAccount = useCallback(() => {
		AccountsService.getCustomerAccountOperation({
			phoneNumber,
			accountType,
			noCache: true,
		})
			.pipe(
				finalize(() => {
					setLoadingAccounts(false);
				})
			)
			.subscribe((customerAccountResponse) => {
				if (customerAccountResponse.customerAccount) {
					setAccount(customerAccountResponse.customerAccount);
				}
			});
	}, [phoneNumber, accountType]);

	const fetchCustomerProducts = useCallback(() => {
		ProductsService.getCustomerProducts({
			phoneNumber,
			accountType,
			billingType,
			resourceInfo: true,
		}).subscribe((res) => {
			setCustomerProducts(res);
		});
	}, [phoneNumber, accountType, billingType]);

	const reloadCustomerAccount = useCallback((): void => {
		setLoadingAccounts(true);
		invalidateCache(['getCustomerAccountOperation', 'getUsage'], true);
		fetchCustomerAccount();
	}, [fetchCustomerAccount]);

	const reloadCustomerProducts = useCallback((): void => {
		invalidateCache(['getCustomerProducts'], true);
		fetchCustomerProducts();
	}, [fetchCustomerProducts]);

	const { state }: any = useLocation();
	/**	added to show aamali+services, in case of micro bundle when we go from numbers list to Number details
		accountCategory is used as an identifier for microbundle */
	useEffect(() => {
		const urlSearchString = window.location.search;
		const params = new URLSearchParams(urlSearchString);
		const accountCategory = params.get(queryParams.AccountCategory) || state?.accountCategory;
		//isNumberAamaliPlus is for aamaliPlus
		const isNumberAamaliPlus: boolean = accountCategory === AAMALI_PLUS_ACCOUNT_CATEGORY;
		setAamaliPlus(isNumberAamaliPlus);
		setAccCategory(accountCategory);
	}, [useLocation, window.location.search]);

	useEffect(() => {
		const accountName: any = () => {
			if (accountType === 'FIX') {
				return translate(`numbers.fixed-line`);
			}
			if (accountType === 'INTERNET') {
				if (accCategory === AAMALI_PLUS_ACCOUNT_CATEGORY) {
					return translate('numbers.aamali-plus-fbb');
				}
				return translate(`numbers.fbb`);
			}
			if (accountType === 'WFBB') {
				return translate(`numbers.wfbb`);
			}
			return translate(`numbers.${accountType.toLowerCase()}-${billingType.toLowerCase()}`);
		};
		setAccountName(accountName);
	});

	useEffect(() => {
		setLoadingCustomerProducts(true);
		ProductsService.getCustomerProducts({
			phoneNumber,
			accountType,
			billingType,
			resourceInfo: true,
		})
			.pipe(
				finalize(() => {
					setLoadingCustomerProducts(false);
				})
			)
			.subscribe(
				(res) => {
					setCustomerProducts(res);
				},
				() => {
					setLoadingCustomerProducts(false);
				}
			);
	}, [phoneNumber, accountType, billingType]);

	useEffect(() => {
		setLoadingAccounts(true);
		AccountsService.getCustomerAccountOperation({
			phoneNumber,
			accountType,
			noCache: true,
		})
			.pipe(
				finalize(() => {
					setLoadingAccounts(false);
				})
			)
			.subscribe(
				(customerAccountResponse) => {
					if (customerAccountResponse.customerAccount) {
						setAccount(customerAccountResponse.customerAccount);
					}
				},
				() => {
					setLoadingAccounts(false);
				}
			);
	}, [phoneNumber, accountType, dispatch]);

	useEffect(() => {
		const subscription = PaymentService.getCommitmentDetails(phoneNumber).subscribe(({ details }) => {
			setCommitmentDetails(details);
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [phoneNumber]);

	useEffect(() => {
		if (!loadingAccounts && !loadingCustomerProducts) {
			dispatch(hideLoadingIndicator());
		} else {
			dispatch(showLoadingIndicator());
		}
		return () => {
			dispatch(hideLoadingIndicator());
		};
	}, [loadingAccounts, loadingCustomerProducts, dispatch]);

	useEffect(() => {
		TagManager.dataLayer({
			dataLayer: {
				event: 'My Services',
				step: 'My Numbers',
				accountID: accId,
				billingID: accountNumber,
				accountNumber: phoneNumber,
				userId: userId,
				language: getLanguage(),
			},
		});
	}, []);

	return (
		<GridContainer>
			<GridItem xs={12}>
				<ConditionalRender
					show={!loadingCustomerProducts && !loadingAccounts}
					onTrue={() => (
						<>
							<CustomNavigationButtons
								className={classes.filters}
								path={path}
								tabs={
									[
										showNumberDetails && {
											value: 'number-details',
											icon: LocalPhoneOutlined,
											action: 'number-details:see',
										},
										showUsage && {
											value: 'usage',
											icon: PhoneInTalkOutlined,
											action: 'number-usage:see',
										},
										showServices && {
											value: 'services',
											icon: Public,
											action: 'number-services:see',
										},
										showMicroBundle && {
											value: 'aamali-services', // a'amali+ services
											icon: Public,
											action: 'number-aaamaliplus-services:see',
										},
										showSimManagement && {
											value: 'sim-management',
											icon: SdCard,
											action: 'number-sim-management:see',
										},
										showSettings && {
											value: 'settings',
											icon: Build,
											action: 'number-settings:see',
										},
										showActiveCommitments && {
											value: 'active-commitments',
											icon: Info,
											action: 'number-active-commitments:see',
										},
									].filter(Boolean) as ITab[]
								}
								value={subpage}
								props={{ accountCategory: accCategory, accountType: accountType }}
							/>
							<ConditionalRender
								show={!loadingCustomerProducts && !loadingAccounts}
								onTrue={() => (
									<Switch>
										{showUsage && (
											<Route
												path={`${match.path}/usage`}
												render={(props: any) => (
													<UsagePage accountName={accountName} {...props} />
												)}
											/>
										)}
										{showServices && (
											<Route
												path={`${match.path}/services`}
												render={(props: any) => (
													<Services accountName={accountName} {...props} />
												)}
											/>
										)}
										{showSimManagement && (
											<Route
												path={`${match.path}/sim-management`}
												render={(props: any) => (
													<SimManagement
														accountName={accountName}
														customerAccount={account}
														reload={reloadCustomerAccount}
														isSimCardDisconnected={isSimCardDisconnected}
														reloadCustomerProducts={reloadCustomerProducts}
														resourceInfo={resourceInfo}
														{...props}
													/>
												)}
											/>
										)}
										{showSettings && (
											<Route
												path={`${match.path}/settings`}
												render={(props: any) => (
													<Settings
														accountName={accountName}
														adslLogin={adslLogin}
														{...props}
													/>
												)}
											/>
										)}
										{showActiveCommitments && (
											<Route
												path={`${match.path}/active-commitments`}
												render={(props: any) => (
													<ActiveCommitments
														accountName={accountName}
														commitmentDetails={commitmentDetails}
														{...props}
													/>
												)}
											/>
										)}
										{showNumberDetails && (
											<Route
												path={showMicroBundle ? `${match.path}/number-details` : match.path}
												render={(props: any) => (
													<NumberDetails
														hasChangePlanPermission={hasPermission(
															userType,
															'upgrade:nama-upgrade'
														)}
														accountName={accountName}
														accountCategory={accountCategory}
														isAccountSuspended={isAccountSuspended}
														isSimCardDisconnected={isSimCardDisconnected}
														customerProducts={customerProducts}
														tariffPlan={customerProducts?.tariffPlan(
															accountType as AccountType
														)}
														tryAndBuyProduct={customerProducts?.tryAndBuyProduct}
														reloadCustomerProducts={reloadCustomerProducts}
														commitmentDetails={commitmentDetails}
														isAamaliPlus={accCategory === AAMALI_PLUS_ACCOUNT_CATEGORY}
														{...props}
													/>
												)}
											/>
										)}
										{showMicroBundle && (
											<Route
												path={match.path} //a'amali+ services
												render={(props: any) => (
													<AamaliServices
														accountName={accountName}
														customerAccount={account}
														tariffPlan={customerProducts?.tariffPlan(
															accountType as AccountType
														)}
														commitmentDetails={commitmentDetails}
														{...props}
													/>
												)}
											/>
										)}
									</Switch>
								)}
							/>
						</>
					)}
				/>
			</GridItem>
		</GridContainer>
	);
};

export default Wrapper(Details);
